import _ from 'lodash';
import urls from '../../../util/urls';
import surveysActionTypes from './actionTypes';
import NotificationHelper from '@lifeomic/phc-web-toolkit/src/redux/util/NotificationHelper';
import { notificationsActions } from '@lifeomic/phc-web-toolkit/src/redux/modules/notifications';
import {
  selectActiveProjectCohortId,
  selectActiveProjectId,
} from '@lifeomic/phc-web-toolkit/src/redux/modules/projectPicker/selectors';
import { selectUserPatientId } from '@lifeomic/phc-web-toolkit/src/redux/modules/userPatients/selectors';

import queryString from 'query-string';

function getResponseContextProps(contextKey: any) {
  if (!contextKey) return null;

  return {
    contextKey,
    parseResponseItems: (data: any) =>
      _.get(data, 'entry', [])
        .map((entry: any) => entry?.resource)
        .filter(Boolean),
    parseNextToken: (data: any) => _.get(data, 'links.next'),
  };
}

function getSurveyServiceContextProps(contextKey: any) {
  if (!contextKey) return null;

  return {
    contextKey,
    parseResponseItems: (data: any) => _.get(data, 'items', []),
    parseNextToken: (data: any) => _.get(data, 'links.next'),
  };
}

function getCreateSurveyContextProps(contextKey: any) {
  if (!contextKey) return null;

  return {
    contextKey,
    parseResponseItems: (data: any) => data,
    parseNextToken: () => null,
  };
}

function fetchSurveys(
  params: any,
  contextKey: any,
  next: any,
  notificationHelper = new NotificationHelper(),
) {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const project = selectActiveProjectId(state);

    let nextUrl;
    if (next) {
      const parts = next.split('?');
      nextUrl = urls.api.surveys.surveys({
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        project,
        params: { ...queryString.parse(parts.pop()) },
      });
    }

    await dispatch({
      type: surveysActionTypes.SURVEYS_FETCH_SURVEYS,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      url: nextUrl || urls.api.surveys.surveys({ project, params }),
      responseContext: getSurveyServiceContextProps(contextKey),
      ...notificationHelper.getAsyncRequestHandlers(),
    });
  };
}

function setDetailSurvey(survey: any) {
  return {
    type: surveysActionTypes.SURVEYS_SET_SURVEY_DETAIL_SURVEY,
    survey,
  };
}

function createSurvey(
  survey: any,
  contextKey: any,
  notificationHelper = new NotificationHelper(),
) {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const project = selectActiveProjectId(state);
    await dispatch({
      type: surveysActionTypes.SURVEYS_CREATE_SURVEY,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      url: urls.api.surveys.surveys({ project }),
      fetchOptions: {
        body: JSON.stringify(survey),
        method: 'POST',
      },
      responseContext: getCreateSurveyContextProps(contextKey),
      ...notificationHelper.getAsyncRequestHandlers(),
    });
  };
}

function updateSurvey(
  survey: any,
  contextKey: any,
  notificationHelper = new NotificationHelper(),
) {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const project = selectActiveProjectId(state);
    await dispatch({
      type: surveysActionTypes.SURVEYS_UPDATE_SURVEY,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      url: urls.api.surveys.surveys({ project, id: survey.id }),
      fetchOptions: {
        body: JSON.stringify(survey),
        method: 'PUT',
      },
      responseContext: getResponseContextProps(contextKey),
      ...notificationHelper.getAsyncRequestHandlers(),
    });
  };
}

function fetchSurvey(
  surveyId: any,
  contextKey: any,
  notificationHelper = new NotificationHelper(),
) {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const project = selectActiveProjectId(state);
    await dispatch({
      type: surveysActionTypes.SURVEYS_FETCH_SURVEY,
      url: urls.api.surveys.surveys({
        id: surveyId,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        project,
      }),
      responseContext: getCreateSurveyContextProps(contextKey),
      ...notificationHelper.getAsyncRequestHandlers(),
    });
  };
}

function createSurveyInviteLink(
  surveyId: any,
  groupId: any,
  contextKey: any,
  notification = new NotificationHelper(),
) {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const project = selectActiveProjectId(state);
    await dispatch({
      type: surveysActionTypes.SURVEYS_CREATE_SHAREABLE_INVITE_LINK,
      url: urls.api.surveys.responses({
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        project,
      }),
      fetchOptions: {
        body: JSON.stringify({
          invite: {
            group: groupId,
          },
          survey: surveyId,
        }),
        method: 'POST',
      },
      responseContext: getCreateSurveyContextProps(contextKey),
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

function updateSurveyResponse(
  surveyResponse: any,
  contextKey: any,
  notification = new NotificationHelper(),
) {
  return async (dispatch: any, getState: any) => {
    if (!surveyResponse || !surveyResponse.id) {
      console.error(
        'Questionnaire response update operation requires a response with an id',
      );
    }

    const state = getState();
    const project = selectActiveProjectId(state);
    const cohort = selectActiveProjectCohortId(state);

    return dispatch({
      type: surveysActionTypes.SURVEYS_UPDATE_RESPONSE,
      url: urls.api.surveys.responses({
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        project,
        id: surveyResponse.id,
        params: cohort ? { cohort } : undefined,
      }),
      fetchOptions: {
        method: 'PUT',
        body: JSON.stringify(surveyResponse),
      },
      responseContext: getResponseContextProps(contextKey),
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

function fetchResponses(
  {
    params = {}, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    next,
  },
  contextKey: any,
  notificationHelper = new NotificationHelper(),
) {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const project = selectActiveProjectId(state) as string;
    const cohort = selectActiveProjectCohortId(state);

    const paramsWithDefaults = {
      ...(params || {}),
      includeSubjectName: true,
      includeSurveyName: true,
    };

    let nextUrl;
    if (next) {
      const parts = next.split('?');
      nextUrl = urls.api.surveys.responses({
        project,
        params: {
          ...queryString.parse(parts.pop()),
          ...paramsWithDefaults,
          cohort,
        },
      });
    }

    await dispatch({
      type: surveysActionTypes.SURVEYS_FETCH_SURVEY_RESPONSES,
      url:
        nextUrl ||
        urls.api.surveys.responses({
          project,
          params: { ...paramsWithDefaults, cohort },
        }),
      responseContext: getSurveyServiceContextProps(contextKey),
      ...notificationHelper.getAsyncRequestHandlers(),
    });
  };
}

function fetchUsersResponses(
  {
    params = {}, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    next,
  },
  contextKey: any,
  notificationHelper = new NotificationHelper(),
) {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const userId = selectUserPatientId(state);

    if (!userId) {
      dispatch(
        notificationsActions.addNotification(
          'You are not associated with any subjects in this project.',
          { statusType: 'error' },
        ),
      );
      return;
    }

    const paramsWithPatientId = {
      ...params,
      patientId: userId,
    };

    await dispatch(
      fetchResponses(
        { params: paramsWithPatientId, next },
        contextKey,
        notificationHelper,
      ),
    );
  };
}

function sendNewUserSurvey(
  subject: any,
  group: any,
  surveyId: any,
  contextKey: any,
  notificationHelper = new NotificationHelper(),
) {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const project = selectActiveProjectId(state);

    await dispatch({
      type: surveysActionTypes.SEND_SURVEY_INVITE_TO_NEW_SUBJECT,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      url: urls.api.surveys.responses({ project }),
      fetchOptions: {
        body: JSON.stringify({
          subject,
          invite: { group },
          survey: surveyId,
        }),
        method: 'POST',
      },
      responseContext: getResponseContextProps(contextKey),
      ...notificationHelper.getAsyncRequestHandlers(),
    });
  };
}

function sendExistingUserSurvey(
  subjectId: any,
  surveyId: any,
  contextKey: any,
  notificationHelper = new NotificationHelper(),
) {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const project = selectActiveProjectId(state);

    await dispatch({
      type: surveysActionTypes.SEND_SURVEY_INVITE_TO_EXISTING_SUBJECT,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      url: urls.api.surveys.responses({ project }),
      fetchOptions: {
        body: JSON.stringify({
          // don't include email or invite here so that no emails will be sent
          // unless there is a user associated with this subject
          subject: { id: subjectId },
          survey: surveyId,
        }),
        method: 'POST',
      },
      onSuccess: notificationHelper.getAsyncRequestSuccessHandler(),
      onFailure: (store: any, data: any) => {
        // ignore INVITATION_ALREADY_EXISTS as it's not a problem in this scenario
        const code = _.get(data, 'code');
        if (code === 'INVITATION_ALREADY_EXISTS') {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          notificationHelper.getAsyncRequestSuccessHandler()(store);
        } else {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          notificationHelper.getAsyncRequestFailureHandler()(store);
        }
      },
      responseContext: getResponseContextProps(contextKey),
    });
  };
}

function createSurveyResponse(
  subjectId: any,
  surveyId: any,
  response: any,
  contextKey: any,
  notificationHelper = new NotificationHelper(),
) {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const project = selectActiveProjectId(state);
    const cohort = selectActiveProjectCohortId(state);

    return await dispatch({
      type: surveysActionTypes.SURVEYS_CREATE_RESPONSE,
      url: urls.api.surveys.responses({
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        project,
        params: cohort ? { cohort } : undefined,
      }),
      fetchOptions: {
        body: JSON.stringify({
          subject: { id: subjectId },
          survey: surveyId,
          response,
        }),
        method: 'POST',
      },
      responseContext: getCreateSurveyContextProps(contextKey),
      ...notificationHelper.getAsyncRequestHandlers(),
    });
  };
}

function deleteSurveyResponse(
  responseId: any,
  contextKey: any,
  notificationHelper = new NotificationHelper(),
) {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const project = selectActiveProjectId(state);

    await dispatch({
      type: surveysActionTypes.SURVEYS_DELETE_RESPONSE,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      url: urls.api.surveys.responses({ project, id: responseId }),
      fetchOptions: {
        method: 'DELETE',
      },
      responseContext: getResponseContextProps(contextKey),
      ...notificationHelper.getAsyncRequestHandlers(),
    });
  };
}

function acceptOpenSurveyInvite(
  inviteId: any,
  accountId: any,
  contextKey: any,
  notificationHelper = new NotificationHelper(),
) {
  return async (dispatch: any) => {
    await dispatch({
      type: surveysActionTypes.SURVEYS_ACCEPT_OPEN_SURVEY_INVITE,
      url: urls.api.surveys.acceptOpenSurveyInvite(inviteId),
      fetchOptions: {
        body: JSON.stringify({
          status: 'ACCEPTED',
        }),
        method: 'PATCH',
      },
      account: accountId,
      responseContext: getResponseContextProps(contextKey),
      ...notificationHelper.getAsyncRequestHandlers(),
    });
  };
}

export default {
  fetchSurvey,
  sendNewUserSurvey,
  fetchResponses,
  updateSurveyResponse,
  createSurvey,
  fetchSurveys,
  updateSurvey,
  setDetailSurvey,
  sendExistingUserSurvey,
  fetchUsersResponses,
  createSurveyResponse,
  createSurveyInviteLink,
  acceptOpenSurveyInvite,
  deleteSurveyResponse,
};
